import { MutableRefObject, useState } from 'react';
import {
  GridAggregationModel,
  GridColDef,
  GridColumnResizeParams,
  GridColumnVisibilityModel,
  GridFilterModel,
  GridGroupNode,
  GridRowGroupingModel,
  GridRowId,
  GridRowParams,
  useGridApiRef,
} from '@mui/x-data-grid-premium';
import { debounce } from '@mui/material';
import { useDispatch } from 'react-redux';
import { enqueueSuccessSnackbar } from '../store/notifications/actionWrapper';
import { GridApiPremium } from '@mui/x-data-grid-premium/models/gridApiPremium';

export type DefaultDatagridSettings = {
  columnsVisibility: GridColumnVisibilityModel;
  aggregationModel: GridAggregationModel;
  groupingModel: GridRowGroupingModel;
  sizeModel: Record<string, number>;
  orderModel: string[];
  filterModel: GridFilterModel;
};

type GridApiRefType = MutableRefObject<GridApiPremium>;

export type DatagridProps = {
  columnVisibilityModel: GridColumnVisibilityModel;
  columnSizeModel: Record<string, number>;
  columnOrderModel: string[];
  groupingColDef: {
    width: number;
  };
  isGroupExpandedByDefault: (node: GridGroupNode) => boolean;
  onColumnVisibilityModelChange: (model: GridColumnVisibilityModel) => void;
  aggregationModel: GridAggregationModel;
  onAggregationModelChange: (newModel: GridAggregationModel) => void;
  rowGroupingModel: GridRowGroupingModel;
  apiRef: GridApiRefType;
  onRowClick: (params: GridRowParams) => void;
  onColumnResize: (params: GridColumnResizeParams) => void;
  onColumnOrderChange: () => void;
  onRowGroupingModelChange: (model: GridRowGroupingModel) => void;
  filterModel: GridFilterModel;
  onFilterModelChange: (newModel: GridFilterModel) => void;
  getAggregationPosition: (
    groupNode: GridGroupNode | null,
  ) => 'footer' | 'inline';
  isRowSelectable: (params: GridRowParams) => boolean;
};

export type UseDatagridSettingsReturn = {
  saveSettings: () => void;
  resetSettings: () => void;
  datagridProps: DatagridProps;
};

const findDifference = (
  biggerArray: string[],
  smallerArray: string[],
): string | undefined =>
  biggerArray.find((item) => !smallerArray.includes(item));

const populateColumnsWithWidth = <T extends GridColDef>(
  columns: T[],
  setup: Record<string, number>,
): T[] =>
  columns.map((column) => ({
    ...column,
    width: setup[column.field],
  }));

export const setupColumnsWidthAndOrder = (columns, setup = {}, order = []) => {
  if (!order || !order.length) return populateColumnsWithWidth(columns, setup);

  return populateColumnsWithWidth(
    [...columns].sort(
      (columnA, columnB) =>
        order.indexOf(columnA.field) - order.indexOf(columnB.field),
    ),
    setup,
  );
};

export const emptySettings: DefaultDatagridSettings = {
  columnsVisibility: {},
  aggregationModel: {},
  groupingModel: [],
  sizeModel: {},
  orderModel: [],
  filterModel: {
    items: [],
  },
};

export const useDatagridSettings = (
  localStorageKey: string,
  apiRef: GridApiRefType | null = null,
  defaultSettings: DefaultDatagridSettings = emptySettings,
): UseDatagridSettingsReturn => {
  const dispatch = useDispatch();
  const newRef = useGridApiRef();
  const gridRef = apiRef ?? newRef;

  const settings: DefaultDatagridSettings =
    JSON.parse(localStorage.getItem(localStorageKey) || 'null') ??
    defaultSettings;

  const [columnsVisibility, setColumnsVisibility] =
    useState<GridColumnVisibilityModel>(settings.columnsVisibility || {});

  const [aggregationModel, setAggregationModel] =
    useState<GridAggregationModel>(settings.aggregationModel || {});

  const [groupingModel, setGroupingModel] = useState<GridRowGroupingModel>(
    settings.groupingModel || [],
  );

  const [filterModel, setFilterModel] = useState<GridFilterModel>(
    settings.filterModel || { items: [] },
  );

  const [sizeModel, setSizeModel] = useState<Record<string, number>>(
    settings.sizeModel || {},
  );

  const [orderModel, setOrderModel] = useState<string[]>(
    settings.orderModel || [],
  );

  const [expanded, setExpanded] = useState<GridRowId[]>([]);

  const columnResizeHandler = (options: GridColumnResizeParams): void => {
    const completeSizeModel = {
      ...sizeModel,
      [options.colDef.field]: options.width,
    };

    setSizeModel(completeSizeModel);
  };

  const orderModelChange = (): void => {
    setOrderModel(
      gridRef.current.getAllColumns().map((column) => column.field),
    );
  };

  const groupingModelChange = (model: GridRowGroupingModel): void => {
    const addition = model.length > groupingModel.length;
    const field = addition
      ? findDifference(model, groupingModel)
      : findDifference(groupingModel, model);

    if (field) {
      setColumnsVisibility((prev) => ({
        ...prev,
        [field]: !addition,
      }));
    }

    setGroupingModel(model);
  };

  const datagridProps: DatagridProps = {
    columnVisibilityModel: columnsVisibility,
    columnSizeModel: sizeModel,
    columnOrderModel: orderModel,
    groupingColDef: {
      width: sizeModel.__row_group_by_columns_group__ || 250,
    },
    isGroupExpandedByDefault: (node: GridGroupNode) =>
      expanded.includes(node.id),
    onColumnVisibilityModelChange: (model: GridColumnVisibilityModel) =>
      setColumnsVisibility(model),
    aggregationModel,
    onAggregationModelChange: (newModel: GridAggregationModel) =>
      setAggregationModel(newModel),
    rowGroupingModel: groupingModel,
    apiRef: gridRef,
    onRowClick: (params: GridRowParams) => {
      if (params.id?.toString().includes('auto-generated-row')) {
        setExpanded((prev) =>
          prev.includes(params.id)
            ? prev.filter((id) => id !== params.id)
            : [...prev, params.id],
        );
      }
    },
    onColumnResize: debounce(columnResizeHandler),
    onColumnOrderChange: orderModelChange,
    onRowGroupingModelChange: (model: GridRowGroupingModel) =>
      groupingModelChange(model),
    filterModel,
    onFilterModelChange: (newModel: GridFilterModel) => {
      setFilterModel(newModel);
    },
    getAggregationPosition: (groupNode: GridGroupNode | null) => {
      return groupNode == null ? 'footer' : 'inline';
    },
    isRowSelectable: (params: GridRowParams) =>
      !params.id?.toString().includes('auto-generated-row'),
  };

  const saveSettings = (): void => {
    const settingsToSave: DefaultDatagridSettings = {
      columnsVisibility,
      aggregationModel,
      groupingModel,
      sizeModel,
      orderModel,
      filterModel,
    };

    localStorage.setItem(localStorageKey, JSON.stringify(settingsToSave));
    dispatch(enqueueSuccessSnackbar('Settings saved'));
  };

  const resetSettings = (): void => {
    localStorage.setItem(localStorageKey, JSON.stringify(defaultSettings));
    setColumnsVisibility(defaultSettings.columnsVisibility);
    setAggregationModel(defaultSettings.aggregationModel);
    setGroupingModel(defaultSettings.groupingModel);
    setSizeModel(defaultSettings.sizeModel);
    setOrderModel(defaultSettings.orderModel);
    setFilterModel(defaultSettings.filterModel);
    dispatch(enqueueSuccessSnackbar('Settings reset'));
  };

  return {
    saveSettings,
    resetSettings,
    datagridProps,
  };
};
